import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';

import { toggleModal, toggleSpinner } from 'apps/auth/actions/appAction';
import { addCardData, updateCardsList } from 'apps/auth/actions/cardsAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { changeFormFieldValue, setNewForm, updateFields } from 'apps/auth/actions/formAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Row } from 'apps/auth/components/common/Layout/Row';
import InputAutoDetect from 'apps/auth/components/form/InputAutoDetect';
import NewCardField from 'apps/auth/components/form/NewCardField';
import Form from 'apps/auth/components/form-multi/Form';
import { clearLeadingSpace } from 'apps/auth/components/form-multi/utils';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { CARD_EXP_DATE, PAN } from 'apps/auth/constants/fieldsName';
import { ADD_SOURCE_BANK_CARD } from 'apps/auth/constants/formName';
import { AUTH_BANK_CARD_ADD, AUTH_BANK_CARD_ADD_ACTION } from 'apps/auth/constants/UrlConstants';
import { USA } from 'apps/auth/constants/WellKnownCountries';
import { apiFetchData } from 'apps/auth/helpers/api';
import { isObject } from 'apps/auth/helpers/utils';

import SameCardLinkedModal from './SameCardLinkedModal';

const AddBankCard = (props) => {
    const modalRef = useRef(null);

    useEffect(() => {
        if (!isObject(props.form)) {
            props.toggleSpinner(true);
            apiFetchData(AUTH_BANK_CARD_ADD)
                .then((json) => {
                    if (json.messages) {
                        props.updatePageMessages(json.messages);
                    }
                    props
                        .addCardData(json.cardForm.card)
                        .then(() => props.setNewForm(ADD_SOURCE_BANK_CARD, getFields(json.cardForm)).then(() => props.toggleSpinner(false)));
                })
                .catch(() => props.toggleSpinner(false));
        }
        return () => {
            if (isObject(props.form) && isObject(props.form.fields)) {
                const clearFields = { ...props.form.fields };
                Object.values(clearFields).forEach((field) => {
                    field.value = '';
                    field.isError = false;
                });
                props.updateFields(ADD_SOURCE_BANK_CARD, clearFields);
            }
        };
    }, []);

    const getFields = (cardForm) => {
        const fields = {};
        if (isObject(cardForm)) {
            const card = cardForm.card;
            fields[card.pan.name] = card.pan;
            if (isObject(card.cardExpired)) {
                fields[card.cardExpired.name] = card.cardExpired;
            }
            if (isObject(card.cvv)) {
                fields[card.cvv.name] = card.cvv;
            }
            if (Array.isArray(cardForm.otherFields)) {
                cardForm.otherFields.forEach((otherField) => (fields[otherField.name] = otherField));
            }
        }
        return fields;
    };

    const addField = (fieldName) => {
        if (
            fieldName === props.card.pan.name ||
            (props.card.cardExpired && fieldName === props.card.cardExpired.name) ||
            (props.card.cvv && fieldName === props.card.cvv.name)
        ) {
            return false;
        }
        return true;
    };

    const submitHandler = (_fields, data) => {
        if (data.errors['toValues[pan]']?.code == 315) {
            modalRef.current?.open(<SameCardLinkedModal modalRef={modalRef} />);
        }

        if (Object.keys(data.errors).length === 0) {
            if (data.cards) {
                props.updateCardsList(data.cards).then(() => props.toggleModal(false));
            }
        }
    };

    const beforeSubmit = () => clearLeadingSpace(props.form.fields, ADD_SOURCE_BANK_CARD, props.changeFormFieldValue);

    const hasErrors = () => {
        if (!isObject(props.form) || !isObject(props.form.fields)) {
            return true;
        }
        return Object.values(props.form.fields).some((field) => field.isError || !field.value);
    };

    if (isObject(props.form)) {
        return (
            <>
                <HeaderPanel
                    title={<>{props.cardsAddBankCardTitle}</>}
                    subtitle={props.userCountry === USA ? props.cardsAddBankCardUsaText : props.cardsAddBankCardText}
                />
                <Form
                    getCanonicalPath={true}
                    formName={ADD_SOURCE_BANK_CARD}
                    fields={props.form.fields}
                    beforeSubmit={beforeSubmit}
                    onSubmitHandler={submitHandler}
                    action={AUTH_BANK_CARD_ADD_ACTION}
                >
                    <Row indent="adaptive-32-48">
                        <NewCardField
                            canShowUsaCardComment={true}
                            binExtraName="binInfo"
                            formName={ADD_SOURCE_BANK_CARD}
                            panName={PAN}
                            redirectByBinUrl={false}
                            expiredDateName={CARD_EXP_DATE}
                        />
                    </Row>
                    {Object.values(props.form.fields).map((field) => {
                        if (addField(field.name)) {
                            return (
                                <Row indent="adaptive-24-32" key={field.name}>
                                    <InputAutoDetect formName={ADD_SOURCE_BANK_CARD} field={field} />
                                </Row>
                            );
                        } else {
                            return false;
                        }
                    })}
                    <FixedBottomPanel isRightAligned>
                        <Button type="submit" raised={true} caption={props.cardsAddBankCardButton} disabled={hasErrors()} />
                    </FixedBottomPanel>
                </Form>
                <ModalNew ref={modalRef} getComponent={(component) => component} />
            </>
        );
    } else {
        return false;
    }
};

const mapStateToProps = (state) => {
    return {
        userCountry: state.auth.countryId,
        toggleModal: state.app.toggleModal,
        form: state.form[ADD_SOURCE_BANK_CARD],
        card: state.cards.cardFields,
        cardsAddBankCardButton: state.messages.pageMessages.cardsAddBankCardButton,
        cardsAddBankCardTitle: state.messages.pageMessages.cardsAddBankCardTitle,
        cardsAddBankCardText: state.messages.pageMessages.cardsAddBankCardText,
        cardsAddBankCardUsaText: state.messages.pageMessages.cardsAddBankCardUsaText,
        yourCardIsLinkedToAnotherAccount: state.messages.pageMessages.yourCardIsLinkedToAnotherAccount,
        pleaseContactUs: state.messages.pageMessages.pleaseContactUs,
        notNowButton: state.messages.pageMessages.notNowButton,
        chatButton: state.messages.pageMessages.chatButton,
    };
};
export default connect(mapStateToProps, {
    setNewForm,
    changeFormFieldValue,
    updateFields,
    updatePageMessages,
    toggleSpinner,
    toggleModal,
    addCardData,
    updateCardsList,
    toggleChat,
})(AddBankCard);
